// Postal code input sanitiser — shared by step 3's shipping address and step
// 4's billing address, so the two can't mask the same kind of code two
// different ways.
//
// A US ZIP is digits only (5, or 5+4 as 12345-6789); a Canadian postal code
// is letters and digits in strict alternation (A1A 1A1), so a character that
// can't be next is dropped rather than accepted and failed later on blur.
// The space is typed for the customer once the fourth character lands.
export function maskPostal(raw, ca) {
  const v = String(raw || '');
  if (!ca) {
    const d = v.replace(/\D+/g, '').slice(0, 9);
    if (d.length > 5) return `${d.slice(0, 5)}-${d.slice(5)}`;
    // Typing the hyphen after five digits is the customer starting a ZIP+4;
    // keep it on screen so the field doesn't appear to swallow the key.
    if (d.length === 5 && /-\s*$/.test(v)) return `${d}-`;
    return d;
  }
  const chars = v.toUpperCase().replace(/[^A-Z0-9]+/g, '').split('');
  let out = '';
  for (const c of chars) {
    const wantLetter = out.length === 0 || out.length === 2 || out.length === 4;
    if (wantLetter ? /[A-Z]/.test(c) : /[0-9]/.test(c)) out += c;
    if (out.length === 6) break;
  }
  return out.length > 3 ? `${out.slice(0, 3)} ${out.slice(3)}` : out;
}

// Reaching for the hyphen after four digits says the code is finished and
// the leading zero was dropped, so restore it and let the +4 carry on from
// there ("2134-" becomes "02134-"). Call with the raw keystroke and the
// already-masked value.
export function restoreZipHyphenLeadingZero(raw, maskedValue, ca) {
  if (ca) return maskedValue;
  if (/-$/.test(raw) && /^\d{4}$/.test(maskedValue)) return `0${maskedValue}-`;
  return maskedValue;
}

// Every ZIP in New England, New Jersey and Puerto Rico starts with a zero,
// and that zero goes missing whenever the value has passed through a
// spreadsheet or an autofill profile filled from one. Four digits under a
// US address is that case, so the zero is restored rather than the customer
// being told the code they were given is invalid. Only on commit — never
// mid-typing, where four digits is simply a code half entered.
export function padZipLeadingZero(value, ca) {
  if (ca) return value;
  const v = String(value || '');
  return /^\d{4}$/.test(v) ? `0${v}` : v;
}

// What a ZIP becomes when the customer has finished with it: on leaving the
// field, and again on Continue / Place order because autofill and paste may
// never blur it. A trailing hyphen (a ZIP+4 started, then abandoned) is dropped,
// so "10080-" commits as the valid 10080 instead of erroring, and a dropped
// leading zero is restored. Canadian codes are left as typed. Only on commit,
// never mid-typing, where "1008" or "10080-" is simply a code half entered.
export function tidyZip(value, ca) {
  const v = String(value || '');
  if (ca) return v;
  const t = v.trim().replace(/-+$/, '');
  return /^\d{4}$/.test(t) ? `0${t}` : t;
}
